import { Helmet } from 'react-helmet';
import styles from './FAQ.module.css';

export default function FAQ() {
  // FAQ Schema markup for Answer Engine Optimization
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase is a modern address book and contact management software that also acts as a personal knowledge management (PKM) hub for your relationships. It helps you organize, track, and manage all your personal and business contacts in one secure location. Unlike social media platforms and cloud-based contact managers, you own 100% of your data and have complete control over your relationships and contact information.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does MyPeopleDatabase cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase offers two pricing options: a one-time purchase of $49.99 for lifetime access, or an annual subscription of $39.99 per year. Both options provide full access to all features including contact management, smart groups, and import/export capabilities.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I import contacts from Gmail or Outlook into MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, MyPeopleDatabase supports importing contacts from multiple sources including Gmail, Outlook, and other contact management systems. You can export your contacts from these services in standard formats like CSV or vCard, then import them directly into MyPeopleDatabase. This allows you to consolidate all your scattered contacts from different platforms into one unified database.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does MyPeopleDatabase work on Mac and Windows?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, MyPeopleDatabase is cross-platform and works on Windows, macOS, and Linux operating systems. The software is built using modern technologies that ensure compatibility across all major desktop platforms, so you can access your contacts regardless of which computer you use.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is MyPeopleDatabase better than Google Contacts?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase offers several advantages over Google Contacts: complete data ownership (your data stays on your device, not in the cloud), advanced organization with unlimited custom groups and nested hierarchies, no dependency on internet connectivity, enhanced privacy controls, and the ability to remove deceased contacts while preserving their records. It also supports a personal knowledge management (PKM) approach by treating each person as a long-lived knowledge node in your system. Unlike Google Contacts, MyPeopleDatabase ensures your relationship data remains exclusively yours and is never monetized or used for advertising.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does MyPeopleDatabase support personal knowledge management (PKM)?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase supports personal knowledge management (PKM) by acting as the people and relationship layer of your PKM stack. Each contact becomes a long-lived knowledge node that you can enrich with details, groups, history, and context over time. Local-first storage, flexible grouping, and planned timeline and notes features are all designed to help you build a people-centered PKM system where you fully own the underlying data.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I organize contacts into groups in MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase features a powerful smart group system that lets you organize contacts in multiple ways. You can create unlimited custom groups (like "Family", "Clients 2024", "Conference Contacts"), assign contacts to multiple groups simultaneously, create nested group hierarchies, and use dynamic filtering to quickly find contacts. Each contact can belong to as many groups as needed, and you can easily manage group memberships through the intuitive interface.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is my data private with MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely. MyPeopleDatabase is built on the principle of complete data ownership and privacy: your contact data is stored locally on your device, not in the cloud. You have full control over who can access your data, and your information is never shared with third parties, used for advertising, or monetized in any way. Unlike social media platforms and free contact managers, your relationships and data remain exclusively yours.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I export my contacts from MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase provides easy export capabilities that allow you to export your contacts in standard formats like CSV or vCard. This ensures you never get locked into the platform and can move your data to other systems if needed. The export feature maintains complete data portability, reinforcing our commitment to your data ownership.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use MyPeopleDatabase offline?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes, MyPeopleDatabase works completely offline since your data is stored locally on your device. You do not need an internet connection to access your contacts, add new people, organize groups, or perform any contact management tasks. This makes it ideal for users who want reliable access to their address book regardless of connectivity.',
        },
      },
      {
        '@type': 'Question',
        name: 'What happens to deceased contacts in MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MyPeopleDatabase offers a thoughtful feature that allows you to remove people who have passed away from your active contact list while still retaining them in the database. This keeps your contact list current and reduces confusion when reaching out to contacts, while preserving memories and maintaining a digital legacy of all your connections.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can multiple people share the same MyPeopleDatabase?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Each MyPeopleDatabase license is designed for individual use, with the data stored on a specific device. However, you can use the export/import features to share contact information between family members or team members, or purchase multiple licenses if you need separate databases for different users.',
        },
      },
    ],
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | MyPeopleDatabase</title>
        <meta
          name="description"
          content="Common questions about MyPeopleDatabase contact manager and personal knowledge management (PKM) hub: pricing, features, import/export, privacy, compatibility with Mac/Windows, and how it compares to Google Contacts."
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Frequently Asked Questions</h1>
        <p className={styles.subtitle}>
          Everything you need to know about MyPeopleDatabase contact manager
        </p>

        <div className={styles.faqContainer}>
          <div className={styles.faqItem}>
            <h2 className={styles.question}>What is MyPeopleDatabase?</h2>
            <p className={styles.answer}>
              MyPeopleDatabase is a modern address book and contact management software that also
              acts as a personal knowledge management (PKM) hub for your relationships. It helps you
              organize, track, and manage all your personal and business contacts in one secure
              location. Unlike social media platforms and cloud-based contact managers, you own 100%
              of your data and have complete control over your relationships and contact
              information.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>How much does MyPeopleDatabase cost?</h2>
            <p className={styles.answer}>MyPeopleDatabase offers two pricing options:</p>
            <ul className={styles.pricingList}>
              <li>
                <strong>One-time purchase:</strong> $49.99 for lifetime access
              </li>
              <li>
                <strong>Annual subscription:</strong> $39.99 per year
              </li>
            </ul>
            <p className={styles.answer}>
              Both options provide full access to all features including contact management, smart
              groups, and import/export capabilities.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>
              Can I import contacts from Gmail or Outlook into MyPeopleDatabase?
            </h2>
            <p className={styles.answer}>
              Yes, MyPeopleDatabase supports importing contacts from multiple sources including
              Gmail, Outlook, and other contact management systems. You can export your contacts
              from these services in standard formats like CSV or vCard, then import them directly
              into MyPeopleDatabase. This allows you to consolidate all your scattered contacts from
              different platforms into one unified database.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>Does MyPeopleDatabase work on Mac and Windows?</h2>
            <p className={styles.answer}>
              Yes, MyPeopleDatabase is cross-platform and works on Windows, macOS, and Linux
              operating systems. The software is built using modern technologies that ensure
              compatibility across all major desktop platforms, so you can access your contacts
              regardless of which computer you use.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>Is MyPeopleDatabase better than Google Contacts?</h2>
            <p className={styles.answer}>
              MyPeopleDatabase offers several key advantages over Google Contacts:
            </p>
            <ul className={styles.featureList}>
              <li>
                <strong>Complete data ownership:</strong> Your data stays on your device, not in the
                cloud
              </li>
              <li>
                <strong>Advanced organization:</strong> Unlimited custom groups and nested
                hierarchies
              </li>
              <li>
                <strong>Offline access:</strong> No dependency on internet connectivity
              </li>
              <li>
                <strong>Enhanced privacy:</strong> Your data is never monetized or used for
                advertising
              </li>
              <li>
                <strong>Deceased contact management:</strong> Keep records while removing from
                active lists
              </li>
            </ul>
            <p className={styles.answer}>
              Unlike Google Contacts, MyPeopleDatabase ensures your relationship data remains
              exclusively yours.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>
              How does MyPeopleDatabase support personal knowledge management (PKM)?
            </h2>
            <p className={styles.answer}>
              MyPeopleDatabase supports personal knowledge management (PKM) by serving as the
              dedicated home for everything you know about the people in your life. Each person can
              be enriched with details, groups, history, and context over time, turning your contact
              list into a people-centered knowledge base that you fully own and control.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>
              How do I organize contacts into groups in MyPeopleDatabase?
            </h2>
            <p className={styles.answer}>
              MyPeopleDatabase features a powerful smart group system that lets you organize
              contacts in multiple ways:
            </p>
            <ul className={styles.featureList}>
              <li>
                Create unlimited custom groups (like "Family", "Clients 2024", "Conference
                Contacts")
              </li>
              <li>Assign contacts to multiple groups simultaneously</li>
              <li>Create nested group hierarchies up to 5 levels deep</li>
              <li>Use dynamic filtering to quickly find contacts</li>
              <li>Manage group memberships through an intuitive interface</li>
            </ul>
            <p className={styles.answer}>
              Learn more about the{' '}
              <a href="/features-groups" className={styles.link}>
                Groups feature
              </a>
              .
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>Is my data private with MyPeopleDatabase?</h2>
            <p className={styles.answer}>
              Absolutely. MyPeopleDatabase is built on the principle of complete data ownership and
              privacy:
            </p>
            <ul className={styles.featureList}>
              <li>Your contact data is stored locally on your device</li>
              <li>Full control over who can access your data</li>
              <li>Never shared with third parties</li>
              <li>Not used for advertising or monetization</li>
              <li>Your relationships remain exclusively yours</li>
            </ul>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>How do I export my contacts from MyPeopleDatabase?</h2>
            <p className={styles.answer}>
              MyPeopleDatabase provides easy export capabilities that allow you to export your
              contacts in standard formats like CSV or vCard. This ensures you never get locked into
              the platform and can move your data to other systems if needed. The export feature
              maintains complete data portability, reinforcing our commitment to your data
              ownership.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>Can I use MyPeopleDatabase offline?</h2>
            <p className={styles.answer}>
              Yes, MyPeopleDatabase works completely offline since your data is stored locally on
              your device. You do not need an internet connection to access your contacts, add new
              people, organize groups, or perform any contact management tasks. This makes it ideal
              for users who want reliable access to their address book regardless of connectivity.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>
              What happens to deceased contacts in MyPeopleDatabase?
            </h2>
            <p className={styles.answer}>
              MyPeopleDatabase offers a thoughtful feature that allows you to remove people who have
              passed away from your active contact list while still retaining them in the database.
              This keeps your contact list current and reduces confusion when reaching out to
              contacts, while preserving memories and maintaining a digital legacy of all your
              connections.
            </p>
          </div>

          <div className={styles.faqItem}>
            <h2 className={styles.question}>
              Can multiple people share the same MyPeopleDatabase?
            </h2>
            <p className={styles.answer}>
              Each MyPeopleDatabase license is designed for individual use, with the data stored on
              a specific device. However, you can use the export/import features to share contact
              information between family members or team members, or purchase multiple licenses if
              you need separate databases for different users.
            </p>
          </div>
        </div>

        <div className={styles.ctaSection}>
          <h2>Still have questions?</h2>
          <p>Contact our support team for personalized assistance.</p>
          <a href="/contact-us" className={styles.ctaButton}>
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
